import * as React from "react";
import styled from "@emotion/styled";
import { Money, ProductProvider } from "@shopify/hydrogen-react";
import type { SelectedOption } from "@shopify/hydrogen-react/storefront-api-types";
import { useSearchParams } from "react-router-dom";
import {
  BlockTag,
  Box,
  ExpandContent,
  FakeLabel,
  FlexRow,
  H1,
  H2,
  IconArrow,
  Link,
  LoadingSpinner,
  NamedAvatar,
  QuantityInput,
  RichText,
  Stack,
  UnexpectedError,
} from "atoms";
import { paths } from "utils/paths";
import { Head } from "features/misc";
import { useArtist } from "features/reviewable";
import { useProductQuery } from "../api/useProductQuery";
import { AddToCart } from "./AddToCart";
import { FeaturedProducts } from "./FeaturedProducts";
import { ProductOptions } from "./ProductOptions";
import { ProductDetailAccordion } from "./ProductDetailAccordion";
import { ProductGallery } from "./ProductGallery";
import { SaleMoney, StrikeMoney } from "./ProductCard";
import { ProductTag } from "./ProductTag";

type ProductDetailsProps = {
  artistId: string;
  handle: string;
};

function ProductDetails({ handle, ...props }: ProductDetailsProps) {
  const [searchParams] = useSearchParams();
  const selectedOptions = React.useMemo(() => {
    const opts: SelectedOption[] = [];
    searchParams.forEach((value, name) => {
      opts.push({ name, value });
    });
    return opts;
  }, [searchParams]);

  const { data, error, isError } = useProductQuery(handle, selectedOptions);

  if (data) {
    return (
      <ProductProvider data={data}>
        <ConnectedProductDetails {...props} product={data} />
      </ProductProvider>
    );
  }

  if (isError) {
    return <UnexpectedError error={error} />;
  }

  return <LoadingSpinner />;
}

type ConnectedProductDetailsProps = {
  product: NonNullable<ReturnType<typeof useProductQuery>["data"]>;
};

function ConnectedProductDetails({
  product,
  ...props
}: ConnectedProductDetailsProps) {
  const {
    artistId,
    descriptionHtml,
    details,
    featuredImage,
    hasVariants,
    id,
    media,
    options,
    selectedVariant,
    title,
  } = product;

  const { data: artist } = useArtist(artistId);

  const [quantity, setQuantity] = React.useState(1);

  // NOTE: line item attributes prefixed with underscore are hidden on Checkout page. Otherwise
  // they'll be listed below the selected options like: `- key: value`
  const lineItemAttrs = React.useMemo(
    () =>
      artist
        ? [
            { key: "_artistId", value: artist.id },
            { key: "_artistName", value: artist.name },
          ]
        : [],
    [artist],
  );

  const availableForSale = !!selectedVariant?.availableForSale;
  // For products with untracked inventory, they will be available for sale but show 0 quantity
  // available. In that case, allow the max safe integer. Otherwise use the quantity available.
  const maxQuantity =
    availableForSale && selectedVariant?.quantityAvailable
      ? selectedVariant?.quantityAvailable
      : Number.MAX_SAFE_INTEGER;

  return (
    <Stack gap="lg" {...props}>
      <Head
        title={`${product.title} | Shop ${artist?.name} on HUG`}
        description={`Shop ${product.title} alongside other physical & digital art from ${artist?.name} on HUG.`}
        socialTitle={`${product.title} | Shop ${artist?.name} on HUG`}
        socialDescription={`Shop ${product.title} alongside other artworks, digital downloads, and more from ${artist?.name} on HUG.`}
        socialImageAltText={product.title}
        socialImage={
          product.featuredImage?.url ??
          artist?.cardImage?.url ??
          artist?.heroImage.url
        }
      />
      <Stack>
        {artistId && (
          <Link size="xs" variant="blank" to={paths.artistShop(artistId)}>
            <IconArrow dir="l" /> Back
          </Link>
        )}

        <ProductWrapper>
          <MediaColumn>
            <ProductGallery
              featuredImage={selectedVariant?.image ?? featuredImage}
              media={media}
            />
          </MediaColumn>
          <ContentColumn gap="md">
            <DetailsBox fullWidth>
              <Stack gap="sm">
                <Stack gap="xs">
                  <H1 size="h3" textTransform="none">
                    {title}
                  </H1>
                  {product.productType && (
                    <ProductTag type={product.productType} size="xs" />
                  )}
                  {artist && (
                    <div>
                      <NamedAvatar
                        name={artist.name}
                        src={artist.heroImage}
                        link={paths.artistProfile(artist.id)}
                      />
                    </div>
                  )}
                </Stack>

                {descriptionHtml && (
                  <ExpandContent showLabel="Read more" hideLabel="Show less">
                    <RichText value={descriptionHtml} />
                  </ExpandContent>
                )}

                <Stack gap="1.33rem">
                  {hasVariants && (
                    <ProductOptions
                      options={options}
                      selectedOptions={selectedVariant?.selectedOptions}
                    />
                  )}

                  <FlexRow
                    justifyContent="space-between"
                    alignItems="flex-start"
                    flexWrap="nowrap"
                    itemsFlex="0 1 auto"
                  >
                    {availableForSale ? (
                      <FakeLabel>
                        <Stack gap="xxs">
                          <div>Quantity</div>
                          <QuantityInput
                            defaultValue={1}
                            onChange={setQuantity}
                            min={1}
                            max={maxQuantity}
                            size="sm"
                          />
                        </Stack>
                      </FakeLabel>
                    ) : (
                      <BlockTag
                        bg="accent4"
                        color="bg"
                        size="md"
                        style={{
                          display: "inline-block",
                          alignSelf: "center",
                        }}
                      >
                        Out of Stock
                      </BlockTag>
                    )}

                    <PriceWrapper>
                      <Stack gap="xs">
                        <FakeLabel>Price</FakeLabel>
                        {selectedVariant && (
                          <>
                            {selectedVariant.compareAtPrice ? (
                              <FlexRow gap="6px">
                                <SaleMoney
                                  data={selectedVariant.price}
                                  productIsOnSale
                                />
                                <StrikeMoney
                                  data={selectedVariant.compareAtPrice}
                                />
                              </FlexRow>
                            ) : (
                              <Money data={selectedVariant.price} />
                            )}
                          </>
                        )}
                      </Stack>
                    </PriceWrapper>
                  </FlexRow>

                  <FlexRow itemsFlex="0 0 100%">
                    <AddToCart
                      attributes={lineItemAttrs}
                      disabled={!availableForSale}
                      quantity={quantity}
                      variantId={selectedVariant?.id}
                    />
                  </FlexRow>
                </Stack>
              </Stack>
            </DetailsBox>
            <Stack gap="0">
              <ProductDetailAccordion ids={details} />
            </Stack>
          </ContentColumn>
        </ProductWrapper>
      </Stack>

      {!!artist && (
        <Stack gap="sm">
          <H2 size="md" style={{ margin: 0 }}>
            More from <span translate="no">{artist.name}</span>
          </H2>

          <FeaturedProducts artistId={artist.id} excluding={id} />
        </Stack>
      )}
    </Stack>
  );
}

const bpProduct = "@media (min-width: 720px)";
const ProductWrapper = styled.div(({ theme }) => ({
  display: "flex",
  gap: theme.spacing.gap,
  alignItems: "stretch",
  flexDirection: "column",
  position: "relative",
  [bpProduct]: {
    flexDirection: "row",
  },
  [theme.breakpoints.desktopMedium]: {
    gap: theme.spacing.gutter,
  },
}));

const MediaColumn = styled.div({
  flex: "1 0 50%",
});

const ContentColumn = styled(Stack)(({ theme }) => ({
  flex: "1 0 300px",
  [bpProduct]: {
    position: "sticky",
    top: theme.spacing.gutter,
  },
}));

const DetailsBox = styled(Box)(({ theme }) => ({
  [bpProduct]: {
    padding: theme.spacing.md,
  },
  [theme.breakpoints.desktop]: {
    padding: theme.spacing.lg,
  },
}));

const PriceWrapper = styled.div(({ theme }) => ({
  color: theme.colors.fg,
  flex: "0 0 auto",
  fontSize: theme.fontSizes.md,
  // matches height of the quantity input
  lineHeight: "30px",
}));

export { ProductDetails };
